import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAllClientUsers } from "../../../api/adminClientManagementApi";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import RequirePermission from "../../../auth/RequirePermission";
import StatusBadge from "../../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../../components/midpoint/admin/users/adminUserFormatters";

function AdminClientUsersPageContent() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await listAllClientUsers({
        page,
        pageSize: 25,
        search: search || undefined,
        status: status || undefined,
      });
      setData(body);
      setErrorMessage("");
    } catch (e) {
      setErrorMessage(e.message || "Unable to load client users.");
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminPageLayout>
      <AdminPageHeader title="Client Users" description="All users across client organizations" />

      <div className="admin-toolbar panel-card">
        <label className="admin-users-field">
          <span>Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </label>
        <label className="admin-users-field">
          <span>Status</span>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INVITED">Invited</option>
            <option value="DISABLED">Disabled</option>
          </select>
        </label>
      </div>

      {loading ? <AdminStatePanel message="Loading users…" /> : null}
      {errorMessage ? <AdminStatePanel tone="error" message={errorMessage} /> : null}

      {data?.users?.length ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Organization</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.clientUserId}>
                  <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
                  <td>{u.email}</td>
                  <td>
                    <Link to={`/admin/client-organizations/${u.clientOrgId}`}>
                      {u.organizationName}
                    </Link>
                  </td>
                  <td>{u.role}</td>
                  <td>
                    <StatusBadge label={u.status} tone="neutral" />
                  </td>
                  <td>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : !loading ? (
        <AdminStatePanel message="No client users found." />
      ) : null}
    </AdminPageLayout>
  );
}

const AdminClientUsersPage = () => (
  <RequirePermission permission="client_admin.view">
    <AdminClientUsersPageContent />
  </RequirePermission>
);

export default AdminClientUsersPage;
